/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import { ReactNode } from 'react'
import { color, fontSize } from '../../utils/Style'
import { Horizon } from '../map_facilities/Horizon'
import { CardSection, Heading } from './CardSection'
import { VStack } from './VStack'

export type PriceOption = {
  title: string
  price: number | string
  note?: string | null
}
export type PriceListProps = {
  heading: string
  options?: PriceOption[]
  note?: string | null
  placeholder?: string
  disableElevation?: boolean
}

export const PriceList = ({
  heading,
  options,
  note,
  placeholder,
  disableElevation,
}: PriceListProps) => {
  const noNote = !note || note.trim() === ''
  const noOptions = !options || options.length === 0
  const hideSelf = noOptions && noNote && !placeholder
  const showPlaceholder = noOptions && placeholder

  if (hideSelf) return null
  if (showPlaceholder)
    return (
      <CardSection disableElevation={disableElevation}>
        <Heading>{heading}</Heading>
        <p
          css={css`
            margin-top: 27px;
            font-size: ${fontSize._14};
            color: ${color.colorBlack};
            line-height: 160%;
          `}
        >
          {placeholder}
        </p>
      </CardSection>
    )
  return (
    <CardSection disableElevation={disableElevation}>
      <Heading>{heading}</Heading>
      <VStack gap="10px">
        {options?.map((option, index) => (
          <OptionRow
            key={`${option.title}_${index}`}
            title={option.title}
            price={option.price}
            note={option.note}
          />
        ))}
      </VStack>
      {!noNote && (
        <p
          css={css`
            margin-top: ${noOptions ? '' : '27px'};
            font-size: 12px;
            color: #7f7d76;
          `}
        >
          {note}
        </p>
      )}
    </CardSection>
  )
}

type OptionRowProps = {
  title: string
  price: number | string | null
  note?: string | null
}
const OptionRow = ({ title, price, note }: OptionRowProps) => {
  const priceText = getPriceText(price)

  return (
    <div css={oprionRowStyle}>
      <Horizon
        justifyContent="inherit"
        columnGap="6px"
        style={{
          gridTemplateColumns: 'auto 1fr auto',
        }}
      >
        <span>{title}</span>
        <span className="dash"></span>
        <span>{priceText}</span>
      </Horizon>
      {note && (
        <div className="note">
          <span className="polygon">
            <Polygon />
          </span>
          <p>{note}</p>
        </div>
      )}
    </div>
  )
}

const oprionRowStyle = css`
  .dash {
    border-top: 1px dashed #aaaaaa;
    height: 0px;
    width: 100%;
  }
  .yen {
    font-size: 10px;
    line-height: 150%;
    margin-left: 2px;
  }
  .note {
    margin-top: 6px;
    position: relative;
    background: #edeceb;
    border-radius: 5px;
    padding: 7px 10px;

    p {
      font-size: 10px;
      line-height: 150%;
    }
  }
  .polygon {
    position: absolute;
    left: 10px;
    top: -15px;
  }
`

const Polygon = () => {
  return <span css={polygonStyle}></span>
}

const polygonStyle = css`
  display: inline-block;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 3.5px 6px 3.5px;
  border-color: transparent transparent #edeceb transparent;
`
function getPriceText(price: number | string | null): string | ReactNode {
  if (typeof price === 'string') return price
  if (price === 0) return '無料'

  return (
    <span>
      {price?.toLocaleString()}
      <span className="yen">円</span>
    </span>
  )
}
